// web/src/components/Modal.tsx — scrim + focus, Esc/back closes, motion enter (reduced-motion safe). D05.
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Icon } from './Icon';

export function Modal({ open, onClose, title, children, size = 'md' }: {
  open: boolean; onClose(): void; title: string; children: ReactNode; size?: 'sm' | 'md' | 'lg';
}) {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);
  const w = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-md';
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 md:items-center md:p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div role="dialog" aria-modal="true" aria-label={title}
            onClick={(e) => e.stopPropagation()}
            className={`w-full ${w} rounded-t-lg border border-hair bg-surface p-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] md:rounded-lg md:pb-5`}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }} exit={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="font-head text-lg text-ink">{title}</h2>
              <button aria-label="Close" onClick={onClose} className="flex size-9 items-center justify-center rounded-md text-muted hover:text-ink"><Icon name="x" size={18} /></button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
